import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthActions, useIsAuthenticated } from "@/hooks/auth/useAuth";
import { useTenant } from "../../contexts/TenantContext";
import { PropertyDetails } from "../../types/property";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { loginSchema, LoginFormData } from "../../schemas/auth";
import GoogleSignInButton from "./GoogleSignInButton";

interface LoginFormProps {
  isLoading?: boolean;
  hasMissingContact?: boolean;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  isLoading: propIsLoading,
  hasMissingContact,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuthActions();
  const isAuthenticated = useIsAuthenticated();
  const { config } = useTenant();

  // Navigate reactively once auth resolves — no timers, no guessing.
  useEffect(() => {
    if (isAuthenticated && !hasMissingContact) {
      const state = location.state as { from?: string };
      navigate(state?.from || "/search", { replace: true });
    }
  }, [isAuthenticated, hasMissingContact, location.state, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: "onChange",
  });

  // Combine loading states
  const formLoading = isLoading || propIsLoading;

  // Default branding values
  const defaultBranding = {
    colors: {
      background: "#ffffff",
      text: "#000000",
      primary: "#000000",
      secondary: "#666666",
    },
    logo: {
      url: "/placeholder-logo.png",
      width: 100,
      height: 50,
    },
    fonts: {
      primary: "system-ui",
    },
  };

  // Use config branding if available, otherwise use defaults
  const branding = config?.branding || defaultBranding;
  const logo = branding.logo ?? defaultBranding.logo;

  const onSubmit = async (data: LoginFormData) => {
    setError(null);
    setIsLoading(true);

    try {
      // Get location state
      const state = location.state as {
        from?: string;
        propertyToSave?: PropertyDetails;
      };

      // Login with property to save if present. Navigation happens reactively
      // in the effect above once auth state resolves.
      await login(
        { email: data.email, password: data.password },
        state?.propertyToSave
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An error occurred during login"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-[calc(100vh-100px)] lg:min-h-[calc(100vh-200px)] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{
        backgroundColor: branding.colors.background,
        color: branding.colors.text,
      }}
    >
      <div className="max-w-md w-full space-y-8">
        <div>
          {/* Logo */}
          <img
            src={logo.url}
            alt={config?.name || "Logo"}
            style={{
              height: logo.height,
              width: "auto",
              maxWidth: logo.width,
              objectFit: "contain",
            }}
            className="mx-auto"
          />
          <h2
            className="mt-6 text-center text-2xl font-bold"
            style={{ color: branding.colors.text }}
          >
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{" "}
            <button
              onClick={() => navigate("/auth/register")}
              className="font-medium hover:opacity-80 !bg-transparent underline p-0 pl-2"
              style={{ color: branding.colors.primary }}
            >
              create a new account
            </button>
          </p>
        </div>

        <div>
          <GoogleSignInButton
            onExistingUser={() => {
              // Existing user is logged in automatically; the effect above
              // navigates reactively once auth state resolves.
            }}
            onNewUser={(data) => {
              // User doesn't exist, show error message
              setError(
                "Account not found. Please register first to create an account."
              );
            }}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            setError={setError}
            config={config}
          />
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div
              className="w-full border-t"
              style={{ borderColor: `${branding.colors.text}33` }}
            ></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span
              className="px-2"
              style={{
                backgroundColor: branding.colors.background,
                color: `${branding.colors.text}99`,
              }}
            >
              Or continue with
            </span>
          </div>
        </div>

        <form noValidate className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                {...register("email")}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--primary)] focus:ring-[var(--primary)] sm:text-sm"
                disabled={formLoading}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.email.message}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Password <span className="text-red-500">*</span>
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                {...register("password")}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--primary)] focus:ring-[var(--primary)] sm:text-sm"
                disabled={formLoading}
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.password.message}
                </p>
              )}
              <div className="mt-1 text-xs text-gray-500">
                Use your phone number as your password
              </div>
            </div>
          </div>

          {error && (
            <p className="text-sm font-medium text-red-600">{error}</p>
          )}

          {hasMissingContact && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    There appears to be an issue with your account. Please
                    contact your website administrator or create a new account
                    to continue accessing all features.
                  </h3>
                </div>
              </div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={formLoading}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
            >
              {formLoading ? "Signing in..." : "Sign In"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginForm;
